/*
  Data transformation pipelines.

  Principles:
  1. We are inspired by SQL and [PRQL](https://prql-lang.org/), but we are not bound by the constraints of SQL.
  2. Our output is HTML tables and Excel tables, so we should be working with those.
  3. Everything should be a simple pipeline of operations, no in-place mutation.
  4. We want this to be primarily UI driven, so no parsing is required (though parsing in theory could be an add-on later).
  5. As much as possible, give the user stable names to work with.

  Initial universe from which all data is derived:
  1. Calculation answers alone (Period, ...release profile specific columns...)
  2. Calculation answers with calculation attributes and outputs (Period, PTEorActual, Release Profile ID, Formula ID, Product ID, CAS, Result, Units)
  3. Product Attributes (Product ID, Name, UsageUnits, SpecificGravity, Solids%Weight, Solids%Volume)
  4. Product Concentrations (Product ID, CAS, Concentration %)
  5. Chemical Attributes (CAS, Name, isTRI, isCarcinogen, isSpecialConcern, isHap)
*/

import { type CalendarRange, type CalendarType, UnitConversionError, UnitsTracker } from '../formulas/units.ts';
import type { CasOrCategory, ProductID, ReleaseProfileID } from '../../types/db.ts';
import type { FormulaID } from '../../types/release-profiles.ts';
import { Tokenizer } from '../formulas/tokenizer.ts';
import { calendarStringParse, Parser } from '../formulas/parser.ts';
import { type ExpressionValidResultTypes, SimpleExpressionEvaluator } from '../formulas/evaluator.ts';
import {
  astWrapPrimitiveValue,
  Binary,
  Bool,
  Identifier,
  Num,
  Str,
  type Expression,
  CalendarValue,
  SimpleEvalError,
} from '../formulas/ast.ts';
import { ExpressionTypeChecker, type TypeCheckResultBase } from '../formulas/expression-type-checker.ts';
import { VariableNameFinder } from '../formulas/variable-name-finder.ts';

// Will just use names as IDs, but don't want plain strings everywhere:
export type TableID = string & { _tag: 'TableID' };
export type ColumnID = string & { _tag: 'ColumnID' };
// TODO: 9. Emissions - Reports - It could be nice for each value to have a "provenance" i.e. not just generic 'number', but something that also says 'this was the density from product ABC', 'this was the output of formula XYZ', etc. This would allow "joins" even without an ID column.
export type ColumnValue =
  | { type: 'number'; value: number; units: UnitsTracker }
  | { type: 'string'; value: string }
  | { type: 'boolean'; value: boolean }
  | { type: 'productID'; productID: ProductID }
  | { type: 'formulaID'; formulaID: FormulaID }
  | { type: 'releaseProfileID'; releaseProfileID: ReleaseProfileID }
  | { type: 'casOrCategory'; casOrCategory: CasOrCategory }
  | { type: 'units'; value: UnitsTracker }
  | { type: 'calendarValue'; value: CalendarValue['value'] };
export type ColumnType = ColumnValue['type'];

// Lookup table for column type operations - consolidates toGroupKey, toPrimitive, and toTypeCheckResult
const columnTypeHandlers: {
  [K in ColumnType]: {
    toGroupKey: (value: Extract<ColumnValue, { type: K }>) => string;
    toPrimitive: (value: Extract<ColumnValue, { type: K }>) => number | string | boolean | CalendarRange;
    toTypeCheckResult: (desc: Extract<ColumnDescriptorSpecific, { type: K }>) => TypeCheckResultBase;
  };
} = {
  number: {
    toGroupKey: (v) => v.value.toString() + ' ' + v.units.toString(),
    toPrimitive: (v) => v.value,
    toTypeCheckResult: (desc) => ({ type: 'number', units: desc.units }),
  },
  string: {
    toGroupKey: (v) => v.value,
    toPrimitive: (v) => v.value,
    toTypeCheckResult: () => ({ type: 'string' }),
  },
  boolean: {
    toGroupKey: (v) => v.value.toString(),
    toPrimitive: (v) => v.value,
    toTypeCheckResult: () => ({ type: 'boolean' }),
  },
  productID: {
    toGroupKey: (v) => v.productID.toString(),
    toPrimitive: (v) => v.productID.toString(),
    toTypeCheckResult: () => ({ type: 'number', units: UnitsTracker.unitless() }),
  },
  formulaID: {
    toGroupKey: (v) => v.formulaID,
    toPrimitive: (v) => v.formulaID,
    toTypeCheckResult: () => ({ type: 'string' }),
  },
  releaseProfileID: {
    toGroupKey: (v) => v.releaseProfileID.toString(),
    toPrimitive: (v) => v.releaseProfileID.toString(),
    toTypeCheckResult: () => ({ type: 'number', units: UnitsTracker.unitless() }),
  },
  casOrCategory: {
    toGroupKey: (v) => v.casOrCategory,
    toPrimitive: (v) => v.casOrCategory,
    toTypeCheckResult: () => ({ type: 'string' }),
  },
  units: {
    toGroupKey: (v) => v.value.toString(),
    toPrimitive: (v) => v.value.toString(),
    toTypeCheckResult: () => ({ type: 'string' }),
  },
  calendarValue: {
    toGroupKey: (v) => v.value.key,
    toPrimitive: (v) => v.value,
    toTypeCheckResult: (desc) => ({ type: 'calendarValue', calendarType: desc.calendarType }),
  },
};

function toGroupKey(value: ColumnValue | null | undefined): string {
  if (value === null || value === undefined) {
    return '\0';
  }

  // TypeScript can't tell we have a value of the correct type here, so we need the any cast:
  return columnTypeHandlers[value.type].toGroupKey(value as any);
}

export function toPrimitive(value: ColumnValue | null | undefined): null | number | string | boolean | CalendarRange {
  if (value === null || value === undefined) {
    return null;
  }

  // TypeScript can't tell we have a value of the correct type here, so we need the any cast:
  return columnTypeHandlers[value.type].toPrimitive(value as any);
}

function toTypeCheckResult<K extends ColumnType>(
  desc: Extract<ColumnDescriptorSpecific, { type: K }>,
): TypeCheckResultBase {
  // TypeScript can't tell we have a config of the correct type here, so we need the any cast:
  return columnTypeHandlers[desc.type].toTypeCheckResult(desc);
}

interface ColumnDescriptorBase {
  columnID: ColumnID;
  synthesizedFrom?: ColumnID; // Used by pivot operations to record the column whose data was used to define this one.
  // TODO: 9. Emissions - Reports - Do we need some kind of friendly name field? Because our identifiers are constrained.
}

type ColumnDescriptorSpecific =
  | {
      type: 'number';
      // TODO: 9. Emissions - Reports - I could imagine we might want something that affects display output stored here (i.e. number of decimal places, how we format the EmissionsPeriod, etc.)
      units: UnitsTracker;
    }
  | {
      type: 'calendarValue';
      calendarType: CalendarType;
    }
  | {
      type: 'string' | 'boolean' | 'productID' | 'formulaID' | 'releaseProfileID' | 'casOrCategory' | 'units';
    };

export type ColumnDescriptor = ColumnDescriptorBase & ColumnDescriptorSpecific;

export type Row = { values: Map<ColumnID, ColumnValue | null>; source: Map<ColumnID, unknown[]> };

/** Create a source map where every column in the row maps to the same source array. */
export function sourceForAllColumns(values: Map<ColumnID, unknown>, sourceItems: unknown[]): Row['source'] {
  const source = new Map<ColumnID, unknown[]>();
  for (const columnID of values.keys()) {
    source.set(columnID, sourceItems);
  }
  return source;
}

export interface Table {
  tableID: TableID;
  columns: Map<ColumnID, ColumnDescriptor>;
  rows: Row[];
}

export type FilterCompareOperator = '=' | '!=' | '<' | '>' | '<=' | '>=';
export type FilterCompareValue = string | number | boolean;

export interface PipelineOperatorConfigs {
  from: {
    type: 'from';
    tableID: TableID;
  };
  select: {
    type: 'select';
    columnIds: ColumnID[];
  };
  exclude: {
    type: 'exclude';
    columnIds: ColumnID[];
  };
  rename: {
    type: 'rename';
    columns: {
      oldColumnID: ColumnID;
      newColumnID: ColumnID;
    }[];
  };
  reorder: {
    type: 'reorder';
    columnIds: ColumnID[];
  };
  compute: {
    type: 'compute';
    newColumnID: ColumnID;
    name: string;
    formula: string;
  };
  filter: {
    type: 'filter';
    formula: string;
  };
  // Like filter, but references the column by ID rather than through a formula, so that any column can be filtered on
  // even if its ID is not a valid formula identifier:
  // Values are interpreted according to the column's type. Numbers are in the column's units, calendar values are
  // strings such as '2024-01'.
  filterCompare: {
    type: 'filterCompare';
    columnID: ColumnID;
  } & (
    | { operator: FilterCompareOperator; value: FilterCompareValue }
    // Keeps rows equal to any of the values:
    | { operator: 'in'; value: FilterCompareValue[] }
  );
  group: {
    type: 'group';
    columnIds: ColumnID[];
    pipelineOperators: PipelineOperatorAnnotated[];
  };
  aggregate: {
    type: 'aggregate';
    columns: {
      sourceColumnID: ColumnID;
      destinationColumnID: ColumnID;
      func: 'sum' | 'avg' | 'min' | 'max' | 'count';
    }[];
  };
  window: {
    type: 'window';
    strict: boolean; // When true, only emit rows if we have the exact window size.
    // These are positive numbers relative to the current row, so if they're both zero we'll have a window size of 1:
    preceding: 'all' | number;
    following: 'all' | number;
    columns: {
      sourceColumnID: ColumnID;
      destinationColumnID: ColumnID;
      func: 'sum' | 'avg' | 'min' | 'max' | 'count';
    }[];
  };
  join: {
    type: 'join';
    leftColumnID: ColumnID;
    rightTableID: TableID;
    rightColumnID: ColumnID;
    joinType: 'inner' | 'left';
  };
  sort: {
    type: 'sort';
    columns: {
      columnID: ColumnID;
      direction: 'asc' | 'desc';
    }[];
  };
  into: {
    type: 'into';
    tableID: TableID;
  };
  pivot: {
    type: 'pivot';
    groupByColumnIds: ColumnID[];
    headerColumnID: ColumnID;
    aggregatedColumnID: ColumnID;
    func: 'sum' | 'avg' | 'min' | 'max' | 'count' | 'first' | 'last';
  };
}

const tableCurrentID: TableID = 'Current' as TableID;
const tableBlank: Table = {
  tableID: 'Blank' as TableID,
  columns: new Map(),
  rows: [],
};

export interface PipelineWarning {
  warningID: `TW${number}`;
  message: string;
}

export type PipelineResult =
  | { type: 'success'; universe: Map<TableID, Table>; table: Table; warnings: PipelineWarning[] }
  | { type: 'failure'; warnings: PipelineWarning[] };

interface AggregationFunc {
  initial: () => number;
  update: (acc: number, value: number) => number;
  finalize: (acc: number, context: { rowCount: number }) => number;
}

const aggregationFuncs = {
  min: {
    initial: () => Number.POSITIVE_INFINITY,
    update: (acc: number, value: number): number => Math.min(acc, value),
    finalize: (acc: number, { rowCount }) => (rowCount === 0 ? 0 : acc),
  },
  max: {
    initial: () => Number.NEGATIVE_INFINITY,
    update: (acc: number, value: number): number => Math.max(acc, value),
    finalize: (acc: number, { rowCount }) => (rowCount === 0 ? 0 : acc),
  },
  sum: {
    initial: () => 0,
    update: (acc: number, value: number): number => acc + value,
    finalize: (acc: number) => acc,
  },
  avg: {
    initial: () => 0,
    update: (acc: number, value: number): number => acc + value,
    finalize: (acc: number, { rowCount }) => (rowCount === 0 ? 0 : acc / rowCount),
  },
  count: {
    initial: () => 0,
    update: (acc: number) => acc + 1,
    finalize: (acc: number) => acc,
  },
} satisfies Partial<Record<string, AggregationFunc>>;

function unitsForAggregation(
  table: Table,
  { func, sourceColumnID }: PipelineOperatorConfigs['aggregate']['columns'][number],
): UnitsTracker {
  if (func === 'count') {
    return UnitsTracker.unitless();
  }

  const column = table.columns.get(sourceColumnID);
  if (column?.type === 'number') {
    return column.units;
  } else {
    return UnitsTracker.unitless();
  }
}

function aggregationHelper(rows: Row[], sourceColumnID: ColumnID, func: AggregationFunc): number | PipelineWarning {
  let current = func.initial();
  let rowCount = 0;

  for (const row of rows) {
    const value = row.values.get(sourceColumnID);
    if (value === null || value === undefined) {
      continue;
    }

    if (value.type !== 'number') {
      return {
        warningID: 'TW1084',
        message: `Column '${sourceColumnID}' contains non-numeric values`,
      };
    }

    rowCount += 1;
    current = func.update(current, value.value);
  }

  current = func.finalize(current, { rowCount });

  return current;
}

function formulaToExpressionAndColumn(
  formula: string,
  columns: Map<ColumnID, ColumnDescriptor>,
): PipelineWarning | { column: ColumnDescriptorSpecific; expression: Expression } {
  const tokenized = Tokenizer.tokenize(formula);
  if ('error' in tokenized) {
    return { warningID: 'TW1086', message: `Error tokenizing formula: ${tokenized.error}` };
  }
  const parsed = Parser.parseExpression(tokenized.tokens);
  if ('error' in parsed) {
    return { warningID: 'TW1087', message: `Error parsing formula: ${parsed.error}` };
  }

  const variablesForTyping = new Map<string, TypeCheckResultBase>();
  for (const [columnID, desc] of columns.entries()) {
    variablesForTyping.set(columnID, toTypeCheckResult(desc));
  }
  const expressionType = new ExpressionTypeChecker(parsed.expression, variablesForTyping).typeCheck();
  if (expressionType.type === 'error') {
    return { warningID: 'TW1088', message: `Error typechecking formula: ${formula}: ${expressionType.error}` };
  }

  let column: ColumnDescriptorSpecific;
  if (expressionType.type === 'number') {
    column = {
      type: 'number',
      units: expressionType.units,
    };
  } else if (expressionType.type === 'string') {
    column = {
      type: 'string',
    };
  } else if (expressionType.type === 'boolean') {
    column = {
      type: 'boolean',
    };
  } else if (expressionType.type === 'calendarValue') {
    column = {
      type: 'calendarValue',
      calendarType: expressionType.calendarType,
    };
  } else if (expressionType.type === 'function') {
    return { warningID: 'TW1089', message: `Unexpected function result in formula: ${formula}` };
  } else {
    const unexpected: never = expressionType.type;
    return { warningID: 'TW1090', message: `Unexpected result type from formula: ${unexpected}` };
  }

  return { column, expression: parsed.expression };
}

function* expressionEvaluateForRows(
  expression: Expression,
  columns: Map<ColumnID, ColumnDescriptor>,
  rows: Row[],
): Generator<{ row: Row; result: ExpressionValidResultTypes }, void, undefined> {
  for (const row of rows) {
    const variables = new Map<string, Num | Str | Bool | CalendarValue>();
    for (const [columnID, desc] of columns.entries()) {
      if (row.values.has(columnID)) {
        const primitive = toPrimitive(row.values.get(columnID));
        if (primitive !== null) {
          variables.set(columnID, astWrapPrimitiveValue(primitive, desc.type === 'number' ? desc.units : null));
        }
      }
    }

    const result = new SimpleExpressionEvaluator(expression, variables).evaluate();
    yield { row, result };
  }
}

/**
 * Keeps the rows for which any of the expressions is true. A referenced column being null for a row counts as false.
 */
function expressionsFilterRows(
  universe: Map<TableID, Table>,
  expressions: Expression[],
  formulaDescription: string,
  table: Table,
): PipelineResult {
  const matchingRows = new Set<Row>();
  for (const expression of expressions) {
    for (const item of expressionEvaluateForRows(expression, table.columns, table.rows)) {
      const failure = expressionFilterResultCheck(item.result, formulaDescription);
      if (failure) {
        return failure;
      } else if (item.result instanceof Bool && item.result.value) {
        matchingRows.add(item.row);
      }
    }
  }

  return {
    type: 'success',
    universe,
    table: {
      tableID: tableCurrentID,
      columns: table.columns,
      // Preserve the original row order:
      rows: table.rows.filter((row) => matchingRows.has(row)),
    },
    warnings: [],
  };
}

function expressionFilterResultCheck(
  result: ExpressionValidResultTypes,
  formulaDescription: string,
): Extract<PipelineResult, { type: 'failure' }> | null {
  if (result instanceof Bool) {
    return null;
  } else if (result instanceof SimpleEvalError) {
    // Callers have already checked that every referenced column exists, so this is a column that is null for this
    // row, which we treat as not matching:
    if (result.details?.type === 'missingVariable') {
      return null;
    }

    return {
      type: 'failure',
      warnings: [{ warningID: 'TW1108', message: `Unexpected error from filtering formula: ${result.message}` }],
    };
  } else {
    return {
      type: 'failure',
      warnings: [
        {
          warningID: 'TW1095',
          message: `Formula must return true or false to act as a filter: ${formulaDescription}`,
        },
      ],
    };
  }
}

function filterCompareLiteral(
  column: ColumnDescriptor,
  value: FilterCompareValue,
): PipelineWarning | Num | Str | Bool | CalendarValue {
  const mismatch: PipelineWarning = {
    warningID: 'TW1139',
    message: `Cannot compare column '${column.columnID}' of type ${column.type} with the value ${JSON.stringify(value)}`,
  };

  switch (column.type) {
    case 'number':
      return typeof value === 'number' ? new Num(value, column.units) : mismatch;
    case 'boolean':
      return typeof value === 'boolean' ? Bool.from(value) : mismatch;
    case 'calendarValue': {
      if (typeof value !== 'string') {
        return mismatch;
      }
      const parsed = calendarStringParse(value, column.calendarType);
      if (parsed instanceof UnitConversionError) {
        return { warningID: 'TW1140', message: `Cannot filter on '${column.columnID}': ${parsed.message}` };
      }
      return new CalendarValue(parsed);
    }
    // These all become strings when evaluated, see toPrimitive:
    case 'string':
    case 'productID':
    case 'formulaID':
    case 'releaseProfileID':
    case 'casOrCategory':
    case 'units':
      return typeof value === 'string' ? new Str(value) : mismatch;
    default: {
      const unexpected: never = column;
      return { warningID: 'TW1141', message: `Unexpected column type: ${JSON.stringify(unexpected)}` };
    }
  }
}

/** One expression per compared value. A row matches the filter if any of them is true. */
function filterCompareToExpressions(
  config: PipelineOperatorConfigs['filterCompare'],
  table: Table,
): PipelineWarning | Binary[] {
  const column = table.columns.get(config.columnID);
  if (!column) {
    return {
      warningID: 'TW1138',
      message: `Column '${config.columnID}' not found in data source '${table.tableID}'`,
    };
  }

  const comparisons: { operator: FilterCompareOperator; value: FilterCompareValue }[] =
    config.operator === 'in'
      ? config.value.map((value) => ({ operator: '=', value }))
      : [{ operator: config.operator, value: config.value }];

  const expressions: Binary[] = [];
  for (const { operator, value } of comparisons) {
    const literal = filterCompareLiteral(column, value);
    if ('warningID' in literal) {
      return literal;
    }

    // We build the expression directly rather than parsing a formula, as column IDs may not be valid identifiers:
    expressions.push(new Binary(new Identifier(config.columnID, ''), operator, literal));
  }

  return expressions;
}

/**
 * The distinct non-null values of a column, each paired with the filterCompare value that matches it exactly. Values
 * are in the order they first appear. Returns null for column types where picking from a list doesn't make sense.
 */
export function tableColumnFilterValues(
  table: Table,
  columnID: ColumnID,
): null | { value: ColumnValue; filterValue: string }[] {
  const column = table.columns.get(columnID);
  if (!column || column.type === 'number' || column.type === 'boolean') {
    return null;
  }

  const results = new Map<string, { value: ColumnValue; filterValue: string }>();
  for (const row of table.rows) {
    const value = row.values.get(columnID);
    if (value === null || value === undefined) {
      continue;
    }

    // Calendar keys are accepted by calendarStringParse, every other remaining type compares as a string:
    const filterValue = value.type === 'calendarValue' ? value.value.key : String(toPrimitive(value));
    if (!results.has(filterValue)) {
      results.set(filterValue, { value, filterValue });
    }
  }

  return Array.from(results.values());
}

// Invariant: Pipeline operators never mutate their tables, they only return new tables.
const pipelineOperators: {
  [ConfigName in keyof PipelineOperatorConfigs]: {
    name: string;
    apply: (universe: Map<TableID, Table>, config: PipelineOperatorConfigs[ConfigName], table: Table) => PipelineResult;
  };
} = {
  from: {
    name: 'From',
    apply(universe, config): PipelineResult {
      const table = universe.get(config.tableID);
      if (!table) {
        return {
          type: 'failure',
          warnings: [{ warningID: 'TW1078', message: `Data source '${config.tableID}' not found` }],
        };
      }

      return { type: 'success', universe, table, warnings: [] };
    },
  },

  select: {
    name: 'Select',
    apply(universe, config, table): PipelineResult {
      const newColumns = new Map<ColumnID, ColumnDescriptor>();

      for (const columnID of config.columnIds) {
        if (!table.columns.has(columnID)) {
          return {
            type: 'failure',
            warnings: [
              {
                warningID: 'TW1079',
                message: `Column '${columnID}' not found in data source '${table.tableID}'`,
              },
            ],
          };
        }

        newColumns.set(columnID, table.columns.get(columnID)!);
      }

      const newRows = table.rows.map((row): Row => {
        const newValues = new Map<ColumnID, ColumnValue | null>();
        const newSource = new Map<ColumnID, unknown[]>();
        for (const columnID of config.columnIds) {
          newValues.set(columnID, row.values.get(columnID) ?? null);
          const s = row.source.get(columnID);
          if (s) {
            newSource.set(columnID, s);
          }
        }

        return { values: newValues, source: newSource };
      });

      return {
        type: 'success',
        universe,
        table: {
          tableID: tableCurrentID,
          columns: newColumns,
          rows: newRows,
        },
        warnings: [],
      };
    },
  },

  exclude: {
    name: 'Exclude',
    apply(universe, config, table): PipelineResult {
      const newColumns = new Map<ColumnID, ColumnDescriptor>(table.columns);

      for (const columnID of config.columnIds) {
        if (!table.columns.has(columnID)) {
          return {
            type: 'failure',
            warnings: [
              {
                warningID: 'TW1080',
                message: `Column '${columnID}' not found in data source '${table.tableID}'`,
              },
            ],
          };
        }

        newColumns.delete(columnID);
      }

      const newRows = table.rows.map((row): Row => {
        const newValues = new Map<ColumnID, ColumnValue | null>(row.values);
        const newSource = new Map<ColumnID, unknown[]>(row.source);
        for (const columnID of config.columnIds) {
          newValues.delete(columnID);
          newSource.delete(columnID);
        }

        return { values: newValues, source: newSource };
      });

      return {
        type: 'success',
        universe,
        table: {
          tableID: tableCurrentID,
          columns: newColumns,
          rows: newRows,
        },
        warnings: [],
      };
    },
  },

  rename: {
    name: 'Rename',
    apply(universe, config, table): PipelineResult {
      const newColumns = new Map<ColumnID, ColumnDescriptor>(table.columns);

      for (const { oldColumnID, newColumnID } of config.columns) {
        if (!newColumns.has(oldColumnID)) {
          return {
            type: 'failure',
            warnings: [
              { warningID: 'TW1081', message: `Column '${oldColumnID}' not found in data source '${table.tableID}'` },
            ],
          };
        }

        newColumns.set(newColumnID, {
          ...newColumns.get(oldColumnID)!,
          columnID: newColumnID,
        });
        newColumns.delete(oldColumnID);
      }

      const newRows = table.rows.map((row): Row => {
        const newValues = new Map<ColumnID, ColumnValue | null>(row.values);
        const newSource = new Map<ColumnID, unknown[]>(row.source);
        for (const { oldColumnID, newColumnID } of config.columns) {
          newValues.set(newColumnID, newValues.get(oldColumnID) ?? null);
          newValues.delete(oldColumnID);
          const s = newSource.get(oldColumnID);
          if (s) {
            newSource.set(newColumnID, s);
          }
          newSource.delete(oldColumnID);
        }

        return { values: newValues, source: newSource };
      });

      return {
        type: 'success',
        universe,
        table: {
          tableID: tableCurrentID,
          columns: newColumns,
          rows: newRows,
        },
        warnings: [],
      };
    },
  },

  reorder: {
    name: 'Reorder',
    apply(universe, config, table): PipelineResult {
      const newColumns = new Map<ColumnID, ColumnDescriptor>();

      for (const columnID of config.columnIds) {
        if (!table.columns.has(columnID)) {
          // We intentionally skip unknown columns so that prefab reports can provide a standard ordering to reports,
          // even in the face of dynamic columns.
          continue;
        }

        newColumns.set(columnID, table.columns.get(columnID)!);
      }

      // Append any columns not explicitly listed, preserving their original order.
      for (const [columnID, descriptor] of table.columns) {
        if (!newColumns.has(columnID)) {
          newColumns.set(columnID, descriptor);
        }
      }

      return {
        type: 'success',
        universe,
        table: {
          tableID: tableCurrentID,
          columns: newColumns,
          rows: table.rows,
        },
        warnings: [],
      };
    },
  },

  compute: {
    name: 'Compute',
    apply(universe, config, table): PipelineResult {
      const formulaInfo = formulaToExpressionAndColumn(config.formula, table.columns);
      if ('warningID' in formulaInfo) {
        return { type: 'failure', warnings: [formulaInfo] };
      }

      const newColumns = new Map<ColumnID, ColumnDescriptor>(table.columns);
      newColumns.set(config.newColumnID, {
        columnID: config.newColumnID,
        ...formulaInfo.column,
      });

      const referencedVars = VariableNameFinder.findInExpression(formulaInfo.expression);

      const newRows: Row[] = [];
      for (const item of expressionEvaluateForRows(formulaInfo.expression, table.columns, table.rows)) {
        const newValues = new Map(item.row.values);

        if (item.result instanceof Num) {
          newValues.set(config.newColumnID, { type: 'number', value: item.result.value, units: item.result.units });
        } else if (item.result instanceof Str) {
          newValues.set(config.newColumnID, { type: 'string', value: item.result.value });
        } else if (item.result instanceof Bool) {
          newValues.set(config.newColumnID, { type: 'boolean', value: item.result.value });
        } else if (item.result instanceof CalendarValue) {
          newValues.set(config.newColumnID, { type: 'calendarValue', value: item.result.value });
        } else if (item.result instanceof Function) {
          return {
            type: 'failure',
            warnings: [{ warningID: 'TW1091', message: `Unexpected function result in formula: ${config.formula}` }],
          };
        } else if (item.result instanceof SimpleEvalError) {
          if (item.result.details?.type === 'missingVariable') {
            // The formula has already been type checked, so this is a column that is null for this row:
            newValues.set(config.newColumnID, null);
          } else {
            return {
              type: 'failure',
              warnings: [{ warningID: 'TW1110', message: `Unexpected error from formula: ${item.result.message}` }],
            };
          }
        } else {
          const unexpected: never = item.result;
          return {
            type: 'failure',
            warnings: [{ warningID: 'TW1092', message: `Unexpected result type from formula: ${unexpected}` }],
          };
        }

        const newSource = new Map<ColumnID, unknown[]>(item.row.source);
        const referencedSources: unknown[] = [];
        for (const varName of referencedVars) {
          const s = item.row.source.get(varName as ColumnID);
          if (s) {
            referencedSources.push(...s);
          }
        }
        newSource.set(config.newColumnID, [...new Set(referencedSources)]);
        newRows.push({ values: newValues, source: newSource });
      }

      return {
        type: 'success',
        universe,
        table: {
          tableID: tableCurrentID,
          columns: newColumns,
          rows: newRows,
        },
        warnings: [],
      };
    },
  },

  filter: {
    name: 'Filter',
    apply(universe, config, table): PipelineResult {
      const formulaInfo = formulaToExpressionAndColumn(config.formula, table.columns);
      if ('warningID' in formulaInfo) {
        return { type: 'failure', warnings: [formulaInfo] };
      }

      if (formulaInfo.column.type !== 'boolean') {
        return {
          type: 'failure',
          warnings: [
            {
              warningID: 'TW1094',
              message: `Formula must return true or false to act as a filter: ${config.formula}`,
            },
          ],
        };
      }

      return expressionsFilterRows(universe, [formulaInfo.expression], config.formula, table);
    },
  },

  filterCompare: {
    name: 'Filter',
    apply(universe, config, table): PipelineResult {
      const expressions = filterCompareToExpressions(config, table);
      if ('warningID' in expressions) {
        return { type: 'failure', warnings: [expressions] };
      }

      const description = `${config.columnID} ${config.operator} ${JSON.stringify(config.value)}`;
      return expressionsFilterRows(universe, expressions, description, table);
    },
  },

  group: {
    name: 'Group',
    apply(universe, config, table): PipelineResult {
      const unitSeparator = '\u001f';
      const grouped = new Map<string, Table['rows']>();

      // Run with an empty set in order to extract the column info even if we have no grouping results:
      const pipelineColumnResult = pipelineApplyMany(
        universe,
        {
          ...table,
          rows: [],
        },
        config.pipelineOperators,
      );
      if (pipelineColumnResult.type === 'failure') {
        return pipelineColumnResult;
      }

      const newColumns = new Map<ColumnID, ColumnDescriptor>();
      for (const [columnID, column] of pipelineColumnResult.table.columns.entries()) {
        newColumns.set(columnID, column);
      }

      for (const columnID of config.columnIds) {
        if (!table.columns.has(columnID)) {
          return {
            type: 'failure',
            warnings: [
              { warningID: 'TW1082', message: `Column '${columnID}' not found in data source '${table.tableID}'` },
            ],
          };
        }

        newColumns.set(columnID, table.columns.get(columnID)!);
      }

      for (const row of table.rows) {
        const key = config.columnIds.map((columnID) => toGroupKey(row.values.get(columnID))).join(unitSeparator);
        if (grouped.has(key)) {
          grouped.get(key)!.push(row);
        } else {
          grouped.set(key, [row]);
        }
      }

      const warnings: PipelineWarning[] = [];
      const newRows: Table['rows'] = [];
      for (const groupedRows of grouped.values()) {
        const pipelineGroupResult = pipelineApplyMany(
          universe,
          {
            tableID: tableCurrentID,
            columns: table.columns,
            rows: groupedRows,
          },
          config.pipelineOperators,
        );
        if (pipelineGroupResult.type === 'failure') {
          return pipelineGroupResult;
        } else if (pipelineGroupResult.warnings.length > 0) {
          warnings.push(...pipelineGroupResult.warnings);
        }

        for (const row of pipelineGroupResult.table.rows) {
          const newValues = new Map(row.values);
          const newSource = new Map<ColumnID, unknown[]>(row.source);
          for (const columnID of config.columnIds) {
            // Invariant: We will always have at least 1 row in the group:
            // Invariant: All rows in the group will have the same value for this column:
            newValues.set(columnID, groupedRows[0].values.get(columnID)!);
            newSource.set(
              columnID,
              groupedRows.flatMap((groupedRow) => groupedRow.source.get(columnID) ?? []),
            );
          }
          newRows.push({ values: newValues, source: newSource });
        }
      }

      return {
        type: 'success',
        universe,
        table: {
          tableID: tableCurrentID,
          columns: newColumns,
          rows: newRows,
        },
        warnings,
      };
    },
  },

  aggregate: {
    name: 'Aggregate',
    apply(universe, config, table): PipelineResult {
      const newColumns = new Map<ColumnID, ColumnDescriptor>();
      const newValues: Row['values'] = new Map();

      for (const column of config.columns) {
        const units = unitsForAggregation(table, column);
        const { sourceColumnID, destinationColumnID, func: funcName } = column;

        if (!table.columns.has(sourceColumnID)) {
          return {
            type: 'failure',
            warnings: [
              {
                warningID: 'TW1097',
                message: `Column '${sourceColumnID}' not found in data source '${table.tableID}'`,
              },
            ],
          };
        }

        // Currently, all aggregations produce numbers, but this may change in the future:
        newColumns.set(destinationColumnID, {
          columnID: destinationColumnID,
          type: 'number',
          units,
        });

        const func = aggregationFuncs[funcName];
        const result = aggregationHelper(table.rows, sourceColumnID, func);
        if (typeof result === 'number') {
          newValues.set(destinationColumnID, {
            type: 'number',
            value: result,
            units,
          });
        } else {
          return {
            type: 'failure',
            warnings: [result],
          };
        }
      }

      return {
        type: 'success',
        universe,
        table: {
          tableID: tableCurrentID,
          columns: newColumns,
          rows: [
            {
              values: newValues,
              source: new Map(
                config.columns.map((col) => [
                  col.destinationColumnID,
                  table.rows.flatMap((r) => r.source.get(col.sourceColumnID) ?? []),
                ]),
              ),
            },
          ],
        },
        warnings: [],
      };
    },
  },

  window: {
    name: 'Window',
    apply(universe, config, table): PipelineResult {
      const newColumns = new Map<ColumnID, ColumnDescriptor>(table.columns);
      for (const column of config.columns) {
        const units = unitsForAggregation(table, column);
        const { sourceColumnID, destinationColumnID } = column;

        if (!table.columns.has(sourceColumnID)) {
          return {
            type: 'failure',
            warnings: [
              {
                warningID: 'TW1098',
                message: `Column '${sourceColumnID}' not found in data source '${table.tableID}'`,
              },
            ],
          };
        }

        // Currently, all aggregations produce numbers, but this may change in the future:
        newColumns.set(destinationColumnID, {
          columnID: destinationColumnID,
          type: 'number',
          units,
        });
      }

      const newRows: Row[] = [];

      for (let rowIndex = 0; rowIndex < table.rows.length; rowIndex++) {
        const precedingIndex = config.preceding === 'all' ? 0 : Math.max(0, rowIndex - config.preceding);
        const followingIndex = config.following === 'all' ? table.rows.length : rowIndex + config.following + 1;

        // In strict mode, we only emit rows if we have enough rows to fulfill the exact window size:
        if (config.strict) {
          if (config.preceding !== 'all' && rowIndex - config.preceding < 0) {
            continue;
          } else if (config.following !== 'all' && followingIndex > table.rows.length) {
            continue;
          }
        }

        const newValues = new Map(table.rows[rowIndex].values);
        const windowRows = table.rows.slice(precedingIndex, followingIndex);

        for (const column of config.columns) {
          const units = unitsForAggregation(table, column);
          const { sourceColumnID, destinationColumnID, func: funcName } = column;
          const func = aggregationFuncs[funcName];
          const result = aggregationHelper(windowRows, sourceColumnID, func);
          if (typeof result === 'number') {
            newValues.set(destinationColumnID, {
              type: 'number',
              value: result,
              units,
            });
          } else {
            return {
              type: 'failure',
              warnings: [result],
            };
          }
        }

        const newSource = new Map<ColumnID, unknown[]>(table.rows[rowIndex].source);
        for (const col of config.columns) {
          newSource.set(
            col.destinationColumnID,
            windowRows.flatMap((r) => r.source.get(col.sourceColumnID) ?? []),
          );
        }
        newRows.push({ values: newValues, source: newSource });
      }

      return {
        type: 'success',
        universe,
        table: {
          tableID: tableCurrentID,
          columns: newColumns,
          rows: newRows,
        },
        warnings: [],
      };
    },
  },

  join: {
    name: 'Join',
    apply(universe, config, leftTable): PipelineResult {
      const rightTable = universe.get(config.rightTableID);
      if (!rightTable) {
        return {
          type: 'failure',
          warnings: [{ warningID: 'TW1085', message: `Data source '${config.rightTableID}' not found` }],
        };
      }

      if (!rightTable.columns.has(config.rightColumnID)) {
        return {
          type: 'failure',
          warnings: [
            {
              warningID: 'TW1099',
              message: `Column '${config.rightColumnID}' not found in data source '${rightTable.tableID}'`,
            },
          ],
        };
      }

      if (!leftTable.columns.has(config.leftColumnID)) {
        return {
          type: 'failure',
          warnings: [
            {
              warningID: 'TW1100',
              message: `Column '${config.leftColumnID}' not found in data source '${leftTable.tableID}'`,
            },
          ],
        };
      }

      // When both sides share the key column's name, it is one column holding the left value, like SQL's USING. Otherwise
      // a left join would blank out the key on unmatched rows:
      const rightColumnIds = Array.from(rightTable.columns.keys()).filter(
        (columnID) => !(columnID === config.leftColumnID && columnID === config.rightColumnID),
      );

      const newColumns = new Map<ColumnID, ColumnDescriptor>(leftTable.columns);
      for (const columnID of rightColumnIds) {
        // TODO: 1. Emissions - Reports - Should we do anything about non-unique columns? Currently they will overwrite any same-named columns in the left table.
        newColumns.set(columnID, rightTable.columns.get(columnID)!);
      }
      const newRows: Row[] = [];

      const rightGrouped = new Map<string, Row[]>();
      for (const row of rightTable.rows) {
        const key = toGroupKey(row.values.get(config.rightColumnID));
        if (rightGrouped.has(key)) {
          rightGrouped.get(key)!.push(row);
        } else {
          rightGrouped.set(key, [row]);
        }
      }

      for (const leftRow of leftTable.rows) {
        const key = toGroupKey(leftRow.values.get(config.leftColumnID));
        const rightRows = rightGrouped.get(key) ?? [];

        for (const rightRow of rightRows) {
          const newValues = new Map(leftRow.values);
          const newSource = new Map<ColumnID, unknown[]>(leftRow.source);
          for (const columnID of rightColumnIds) {
            newValues.set(columnID, rightRow.values.get(columnID) ?? null);
            const s = rightRow.source.get(columnID);
            if (s) {
              newSource.set(columnID, s);
            }
          }
          newRows.push({ values: newValues, source: newSource });
        }

        if (config.joinType === 'left' && rightRows.length === 0) {
          const newValues = new Map(leftRow.values);
          const newSource = new Map<ColumnID, unknown[]>(leftRow.source);
          for (const columnID of rightColumnIds) {
            newValues.set(columnID, null);
            newSource.set(columnID, []);
          }
          newRows.push({ values: newValues, source: newSource });
        }
      }

      return {
        type: 'success',
        universe,
        table: { tableID: tableCurrentID, columns: newColumns, rows: newRows },
        warnings: [],
      };
    },
  },

  sort: {
    name: 'Sort',
    apply(universe, config, table): PipelineResult {
      if (config.columns.length === 0) {
        return { type: 'success', universe, table, warnings: [] };
      }

      for (const { columnID } of config.columns) {
        if (!table.columns.has(columnID)) {
          return {
            type: 'failure',
            warnings: [
              {
                warningID: 'TW1101',
                message: `Column '${columnID}' not found in data source '${table.tableID}'`,
              },
            ],
          };
        }
      }

      const newRows = table.rows.slice();
      newRows.sort((a, b) => {
        for (const { columnID, direction } of config.columns) {
          const aVal = a.values.get(columnID);
          const bVal = b.values.get(columnID);

          let diff: number;
          if (aVal?.type === 'number' && bVal?.type === 'number') {
            diff = direction === 'asc' ? aVal.value - bVal.value : bVal.value - aVal.value;
          } else {
            const aStr = toGroupKey(aVal);
            const bStr = toGroupKey(bVal);
            diff = direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
          }

          if (diff !== 0) {
            return diff;
          }
        }

        return 0;
      });

      return {
        type: 'success',
        universe,
        table: { tableID: tableCurrentID, columns: table.columns, rows: newRows },
        warnings: [],
      };
    },
  },

  into: {
    name: 'Into',
    apply(universe, config, table): PipelineResult {
      if (universe.has(config.tableID)) {
        return {
          type: 'failure',
          warnings: [{ warningID: 'TW1083', message: `Data source '${config.tableID}' already exists.` }],
        };
      }

      const tableRenamed = { tableID: config.tableID, columns: table.columns, rows: table.rows };
      const newUniverse = new Map(universe);
      newUniverse.set(config.tableID, tableRenamed);
      return { type: 'success', universe: newUniverse, table, warnings: [] };
    },
  },

  pivot: {
    name: 'Pivot',
    apply(universe, config, table): PipelineResult {
      // Validate row columns exist:
      for (const columnID of config.groupByColumnIds) {
        if (!table.columns.has(columnID)) {
          return {
            type: 'failure',
            warnings: [
              {
                warningID: 'TW1102',
                message: `Group by column '${columnID}' not found in data source '${table.tableID}'`,
              },
            ],
          };
        }
      }

      // Validate pivot column exists:
      if (!table.columns.has(config.headerColumnID)) {
        return {
          type: 'failure',
          warnings: [
            {
              warningID: 'TW1103',
              message: `Header column '${config.headerColumnID}' not found in data source '${table.tableID}'`,
            },
          ],
        };
      }

      // Validate value column exists:
      if (!table.columns.has(config.aggregatedColumnID)) {
        return {
          type: 'failure',
          warnings: [
            {
              warningID: 'TW1104',
              message: `Aggregated column '${config.aggregatedColumnID}' not found in data source '${table.tableID}'`,
            },
          ],
        };
      }

      const aggregatedColumnDescriptor = table.columns.get(config.aggregatedColumnID)!;
      const valueUnits =
        aggregatedColumnDescriptor.type === 'number' ? aggregatedColumnDescriptor.units : UnitsTracker.unitless();

      // Collect all unique pivot values to create column headers:
      const headerValues = new Set<string>();
      for (const row of table.rows) {
        const pivotValue = row.values.get(config.headerColumnID);
        if (pivotValue !== null && pivotValue !== undefined) {
          headerValues.add(toGroupKey(pivotValue));
        }
      }
      const sortedHeaderValues = Array.from(headerValues).sort();

      // Check for column name collisions between header values and group-by columns:
      for (const headerValue of sortedHeaderValues) {
        if (config.groupByColumnIds.includes(headerValue as ColumnID)) {
          return {
            type: 'failure',
            warnings: [
              {
                warningID: 'TW1105',
                message: `Header value '${headerValue}' conflicts with group-by column name`,
              },
            ],
          };
        }
      }

      // Build new columns: group by columns + one column per pivot value:
      const newColumns = new Map<ColumnID, ColumnDescriptor>();
      for (const columnID of config.groupByColumnIds) {
        newColumns.set(columnID, table.columns.get(columnID)!);
      }
      for (const headerValue of sortedHeaderValues) {
        const headerColumnID = headerValue as ColumnID;
        if (config.func === 'count') {
          newColumns.set(headerColumnID, {
            columnID: headerColumnID,
            type: 'number',
            units: UnitsTracker.unitless(),
            synthesizedFrom: config.headerColumnID,
          });
        } else {
          newColumns.set(headerColumnID, {
            ...aggregatedColumnDescriptor,
            columnID: headerColumnID,
            synthesizedFrom: config.headerColumnID,
          });
        }
      }

      // Group rows by row columns:
      const unitSeparator = '\u001f';
      const grouped = new Map<string, Row[]>();
      for (const row of table.rows) {
        const key = config.groupByColumnIds.map((columnID) => toGroupKey(row.values.get(columnID))).join(unitSeparator);
        if (grouped.has(key)) {
          grouped.get(key)!.push(row);
        } else {
          grouped.set(key, [row]);
        }
      }

      // Build new rows:
      const newRows: Row[] = [];
      for (const groupedRows of grouped.values()) {
        const newValues: Row['values'] = new Map();

        // Copy row column values from first row in group (they're all the same for these columns):
        for (const columnID of config.groupByColumnIds) {
          newValues.set(columnID, groupedRows[0].values.get(columnID) ?? null);
        }

        const rowsByHeaderValue = new Map<string, Row[]>();
        for (const row of groupedRows) {
          const headerValue = toGroupKey(row.values.get(config.headerColumnID));
          if (rowsByHeaderValue.has(headerValue)) {
            rowsByHeaderValue.get(headerValue)!.push(row);
          } else {
            rowsByHeaderValue.set(headerValue, [row]);
          }
        }

        // For each pivot value, aggregate the matching values:
        for (const headerValue of sortedHeaderValues) {
          const headerColumnID = headerValue as ColumnID;
          const matchingRows = rowsByHeaderValue.get(headerValue) ?? [];

          if (matchingRows.length === 0) {
            newValues.set(headerColumnID, null);
            continue;
          }

          // Apply aggregation:
          if (config.func === 'first') {
            newValues.set(headerColumnID, matchingRows[0].values.get(config.aggregatedColumnID) ?? null);
          } else if (config.func === 'last') {
            newValues.set(
              headerColumnID,
              matchingRows[matchingRows.length - 1].values.get(config.aggregatedColumnID) ?? null,
            );
          } else {
            // Use standard aggregation funcs for sum, avg, min, max, count:
            const func = aggregationFuncs[config.func];
            const result = aggregationHelper(matchingRows, config.aggregatedColumnID, func);
            if (typeof result === 'number') {
              newValues.set(headerColumnID, {
                type: 'number',
                value: result,
                units: config.func === 'count' ? UnitsTracker.unitless() : valueUnits,
              });
            } else {
              return {
                type: 'failure',
                warnings: [result],
              };
            }
          }
        }

        const newSource = new Map<ColumnID, unknown[]>();
        for (const columnID of config.groupByColumnIds) {
          newSource.set(
            columnID,
            groupedRows.flatMap((r) => r.source.get(columnID) ?? []),
          );
        }
        for (const headerValue of sortedHeaderValues) {
          const headerColumnID = headerValue as ColumnID;
          const matchingRows = rowsByHeaderValue.get(headerValue) ?? [];
          newSource.set(
            headerColumnID,
            matchingRows.flatMap((r) => r.source.get(config.aggregatedColumnID) ?? []),
          );
        }
        newRows.push({ values: newValues, source: newSource });
      }

      return {
        type: 'success',
        universe,
        table: {
          tableID: tableCurrentID,
          columns: newColumns,
          rows: newRows,
        },
        warnings: [],
      };
    },
  },
};

export type PipelineOperatorAnnotated = {
  notes: string;
  config: PipelineOperatorConfigs[keyof PipelineOperatorConfigs];
};

function pipelineApplyMany(
  universe: Map<TableID, Table>,
  table: Table,
  list: PipelineOperatorAnnotated[],
): PipelineResult {
  const warnings: PipelineWarning[] = [];
  for (const { config } of list) {
    // TODO: 9. Emissions - Reports - If you could somehow hash the universe+current table+config, you could cache the results:
    let result: PipelineResult;
    try {
      // TypeScript can't tell we have a config of the correct type here, so we need the any cast:
      result = pipelineOperators[config.type].apply(universe, config as any, table);
    } catch (e: any) {
      return {
        type: 'failure',
        warnings: [{ warningID: 'TW1096', message: `Error evaluating pipeline operator: ${e.message}` }],
      };
    }
    if (result.type === 'failure') {
      return result;
    } else {
      warnings.push(...result.warnings);
    }

    universe = result.universe;
    table = result.table;
  }

  return {
    type: 'success',
    universe,
    table,
    warnings,
  };
}

export interface Report {
  name: string;
  pipelineOperators: PipelineOperatorAnnotated[];
}

/**
 * Run a report pipeline starting from an initial universe of tables.
 * The first operator should typically be a 'from' to select which table to start with.
 */
export function reportPipelinesRun(report: Report, initialUniverse: Map<TableID, Table>): PipelineResult {
  return pipelineApplyMany(initialUniverse, tableBlank, report.pipelineOperators);
}

interface BlankEvaluationContext {
  type: 'blank';
  universe: Map<TableID, Table>;
  table: Table;
}

function tableToBlank(table: Table): Table {
  return {
    tableID: table.tableID,
    columns: table.columns,
    rows: [],
  };
}

export function contextToBlank(universe: Map<TableID, Table>, current: Table = tableBlank): BlankEvaluationContext {
  return {
    type: 'blank',
    universe: new Map(Array.from(universe.entries()).map(([tableID, table]) => [tableID, tableToBlank(table)])),
    table: tableToBlank(current),
  };
}

function contextToNext(
  context: BlankEvaluationContext,
  config: PipelineOperatorAnnotated['config'],
): BlankEvaluationContext | { type: 'warning'; warning: PipelineWarning } {
  let result: PipelineResult;
  try {
    // TypeScript can't tell we have a config of the correct type here, so we need the any cast:
    result = pipelineOperators[config.type].apply(context.universe, config as any, context.table);
  } catch (e: any) {
    // TODO: 3. Emissions - Reports - Expression evaluation can throw if the expression is malformed, it would be nice if SimpleExpressionEvaluator just returned an error result instead:
    return {
      type: 'warning',
      warning: { warningID: 'TW1107', message: `Error evaluating pipeline operator: ${e.message}` },
    };
  }
  if (result.type === 'failure') {
    return { type: 'warning', warning: result.warnings[0] };
  }

  return {
    type: 'blank',
    universe: result.universe,
    table: result.table,
  };
}

export type TraceSourced = { type: 'sourced'; tableID: TableID; columnID: ColumnID };
export type TraceTransformation =
  | { type: 'compute'; newColumnID: ColumnID; formula: string; parent: TraceSourced | TraceTransformation }
  | {
      type: 'aggregate';
      destinationColumnID: ColumnID;
      func: 'sum' | 'avg' | 'min' | 'max' | 'count' | 'first' | 'last';
      parent: TraceSourced | TraceTransformation;
    };
export type TraceOutput = {
  type: 'output';
  tableID: TableID;
  columnID: ColumnID;
  synthesizedFrom?: ColumnID;
  parent: TraceSourced | TraceTransformation;
};
type TraceWarning = { type: 'warning'; warning: PipelineWarning };

export function pipelineFindTransformations(
  contextStart: BlankEvaluationContext,
  list: PipelineOperatorAnnotated[],
  tracingOrig: { tableID: TableID; columnID: ColumnID },
  tracingNow: {
    tableID: TableID;
    columnID: ColumnID;
    synthesizedFrom?: ColumnID;
    parent: TraceSourced | TraceTransformation;
  } | null,
  results: (TraceOutput | TraceWarning)[],
): (TraceOutput | TraceWarning)[] {
  // If we've run out of pipeline operators to apply, we're done:
  if (list.length === 0) {
    if (tracingNow) {
      results.push({ type: 'output', ...tracingNow });
    }
    return results;
  }

  const [{ config }, ...remaining] = list;

  // Attempt to produce the next context, fail early with a warning if we can't:
  const contextNext = contextToNext(contextStart, config);
  if (contextNext.type === 'warning') {
    results.push(contextNext);
    return results;
  }

  if (config.type === 'from') {
    // If we're selecting from the table we're looking for, we move into the tracing state:
    if (config.tableID === tracingOrig.tableID) {
      return pipelineFindTransformations(
        contextNext,
        remaining,
        tracingOrig,
        {
          parent: { type: 'sourced', ...tracingOrig },
          ...tracingOrig,
        },
        results,
      );
    } else {
      return pipelineFindTransformations(contextNext, remaining, tracingOrig, null, results);
    }
  } else if (config.type === 'into') {
    if (tracingNow) {
      // We must now follow both the current table and the table 'into' took a snapshot of:
      pipelineFindTransformations(contextNext, remaining, tracingOrig, tracingNow, results);
      pipelineFindTransformations(
        contextNext,
        remaining,
        { tableID: config.tableID, columnID: tracingNow.columnID },
        null,
        results,
      );
      return results;
    } else {
      return pipelineFindTransformations(contextNext, remaining, tracingOrig, null, results);
    }
  } else if (config.type === 'join') {
    pipelineFindTransformations(contextNext, remaining, tracingOrig, tracingNow, results);
    if (config.rightTableID === tracingOrig.tableID) {
      pipelineFindTransformations(
        contextNext,
        remaining,
        tracingOrig,
        {
          tableID: contextNext.table.tableID,
          columnID: tracingOrig.columnID,
          // TODO: 9. Emissions - If the right table was created via 'into', we won't fully trace it here. Joins now have
          //  a UI and can join snapshots, so drilldowns on those columns may be incomplete:
          parent: { type: 'sourced', tableID: config.rightTableID, columnID: tracingOrig.columnID },
        },
        results,
      );
    }
    return results;
  }

  // If we're not currently operating on a table we're tracing, we can just keep working through the pipeline:
  if (!tracingNow) {
    return pipelineFindTransformations(contextNext, remaining, tracingOrig, null, results);
  }

  if (
    config.type === 'filter' ||
    config.type === 'filterCompare' ||
    config.type === 'reorder' ||
    config.type === 'sort'
  ) {
    // These operators do not affect any of the columns, and so we continue as-is:
    return pipelineFindTransformations(contextNext, remaining, tracingOrig, tracingNow, results);
  } else if (config.type === 'select') {
    return pipelineFindTransformations(
      contextNext,
      remaining,
      tracingOrig,
      config.columnIds.includes(tracingNow.columnID) ? tracingNow : null,
      results,
    );
  } else if (config.type === 'exclude') {
    return pipelineFindTransformations(
      contextNext,
      remaining,
      tracingOrig,
      config.columnIds.includes(tracingNow.columnID) ? null : tracingNow,
      results,
    );
  } else if (config.type === 'rename') {
    const foundRename = config.columns.find((c) => c.oldColumnID === tracingNow.columnID);
    return pipelineFindTransformations(
      contextNext,
      remaining,
      tracingOrig,
      foundRename
        ? { tableID: tracingNow.tableID, columnID: foundRename.newColumnID, parent: tracingNow.parent }
        : tracingNow,
      results,
    );
  } else if (config.type === 'compute') {
    const formulaAffectsUs = VariableNameFinder.findInStringIgnoringErrors(config.formula).has(tracingNow.columnID);
    // Continue tracing the current column, as it still exists:
    pipelineFindTransformations(contextNext, remaining, tracingOrig, tracingNow, results);
    if (formulaAffectsUs) {
      pipelineFindTransformations(
        contextNext,
        remaining,
        tracingOrig,
        {
          tableID: tracingNow.tableID,
          columnID: config.newColumnID,
          parent: {
            type: 'compute',
            newColumnID: config.newColumnID,
            formula: config.formula,
            parent: tracingNow.parent,
          },
        },
        results,
      );
    }
    return results;
  } else if (config.type === 'aggregate') {
    const found = config.columns.filter((c) => c.sourceColumnID === tracingNow.columnID);
    for (const agg of found) {
      pipelineFindTransformations(
        contextNext,
        remaining,
        tracingOrig,
        {
          tableID: tracingNow.tableID,
          columnID: agg.destinationColumnID,
          parent: {
            type: 'aggregate',
            destinationColumnID: agg.destinationColumnID,
            func: agg.func,
            parent: tracingNow.parent,
          },
        },
        results,
      );
    }
    return results;
  } else if (config.type === 'window') {
    // Continue tracing the current column, as it still exists:
    pipelineFindTransformations(contextNext, remaining, tracingOrig, tracingNow, results);
    const relevantColumns = config.columns.filter((c) => c.sourceColumnID === tracingNow.columnID);
    for (const column of relevantColumns) {
      pipelineFindTransformations(
        contextNext,
        remaining,
        tracingOrig,
        {
          tableID: tracingNow.tableID,
          columnID: column.destinationColumnID,
          parent: {
            type: 'aggregate',
            destinationColumnID: column.destinationColumnID,
            func: column.func,
            parent: tracingNow.parent,
          },
        },
        results,
      );
    }
    return results;
  } else if (config.type === 'group') {
    // If we group by our tracing target, follow it through to the end:
    pipelineFindTransformations(
      contextNext,
      remaining,
      tracingOrig,
      config.columnIds.includes(tracingNow.columnID) ? tracingNow : null,
      results,
    );

    // Also see if our group's inner pipeline produces any output for what we're tracing, and follow that through:
    const aggregationOutputs = pipelineFindTransformations(
      contextStart,
      config.pipelineOperators,
      tracingOrig,
      tracingNow,
      [],
    );
    for (const warningOrOutput of aggregationOutputs) {
      if (warningOrOutput.type === 'output') {
        pipelineFindTransformations(
          contextNext,
          remaining,
          tracingOrig,
          {
            tableID: warningOrOutput.tableID,
            columnID: warningOrOutput.columnID,
            parent: warningOrOutput.parent,
          },
          results,
        );
      } else {
        results.push(warningOrOutput);
      }
    }
    return results;
  } else if (config.type === 'pivot') {
    // We intentionally ignore headerColumnID as you can't apply transformations to a header, and the user cannot
    // trace column header names in the UI.

    if (config.groupByColumnIds.includes(tracingNow.columnID)) {
      pipelineFindTransformations(contextNext, remaining, tracingOrig, tracingNow, results);
    }

    if (config.aggregatedColumnID === tracingNow.columnID) {
      // Since headers are based on values, we don't have a real column ID to use. Generally pivots are the final
      // step of a pipeline, and we're recording the source in synthesizedFrom, so hopefully this will be sufficient
      // for most cases:
      const destinationColumnID = `__PIVOT__[${config.headerColumnID}]` as ColumnID;
      pipelineFindTransformations(
        contextNext,
        remaining,
        tracingOrig,
        {
          tableID: tracingNow.tableID,
          columnID: destinationColumnID,
          synthesizedFrom: config.headerColumnID,
          parent: {
            type: 'aggregate',
            destinationColumnID,
            func: config.func,
            parent: tracingNow.parent,
          },
        },
        results,
      );
    }
    return results;
  } else {
    const unexpected: never = config;
    console.error('Unexpected pipeline operator type:', unexpected);
    return results;
  }
}
